import { useCallback, useEffect, useRef, useState } from 'react';
import { getDropContext, previewDocumentName, placeDealDocument, announceDocumentsChanged } from '@/services/deals.service.js';
import { listDealInterests } from '@/services/interests.service.js';
import { sha256OfFile } from '@/lib/file-sha256.js';
import { dropKind, suggestDropKind } from '@/lib/dropKinds.js';
import { toast } from '@/components/ui/toaster.jsx';

// THE DROP'S STATE, ONE PLACE (paperwork-1 D7, A18): the files a broker dropped on a deal and the answers each still needs.
// Used by the deal page's page-wide drop dialog and the policy-docs sheet (screen 2). Each entry carries its file, its kind
// (a filename may preselect it), its second answer (the ledger row or the holder), the replace answer, the server's preview
// (the derived name, our own paper, what it replaces) and, after filing, the server's answer for it. The client derives
// nothing: the name, the our-paper finding, the pickable ledger rows and the candidates are the server's.
//
// READY means every file has a kind, every replace question the server asked has an answer, and no carrier kind is chosen for
// a file the server says is our own paper. A quote is routed by the caller (the quote parse), never filed here.
// SETTLED (L1b-B4, Chrome pass): a file is settled once it is filed, or when the preview says its bytes are already filed on
// a row that states every fact a drop could add (`already_filed.settled`, the server's rule): such a file is never posted
// (the door would refuse it, or re-upload a signed application already filed), never asked a kind, and only removed.
// `pending` is every file that is not settled; callers post and count it, never the raw entries.

// REFUSED AT THE DROP (2026-11-02, David's step 15 finding 3): a file the server's preview says cannot be filed (its `storable`
// answer: an executable, an unkept type, no extension, over the cap) is taken off the list with ONE toast naming the file and
// why, so the button never counts a file that will not be filed. ONLY that answer refuses a file (review of 2026-11-02, finding
// I2): a preview that FAILED (the deal has no policy record, the store did not answer, anything else) keeps the file on the
// list, uncounted and unposted, its row saying it could not be checked; a deal with no policy record is said once, above the
// rows (the drop context's answer), never once per file. A kind the server says this file
// cannot be (kind_rules: a PDF-only kind for another type) is withdrawn when it was only preselected, and the row shows why.
// TWO DOCUMENTS NEVER SHARE A NAME: each preview carries what the earlier files of this drop will be filed under (the server's
// own earlier answers: `taken_base` their plain names, `taken` their names for a signed application) and the replace answer,
// and the server gives the name its place by the list reader's own rule (review of 2026-11-02, finding I3); a change to an
// earlier file or to the replace answer asks again. A file that will not become a new document (settled, already filed, or
// not checked) takes no place.

const NO_POLICY_ROW = 'NO_POLICY_ROW';

let seq = 0;
const takesAPlace = (x) => !x.result?.ok && x.kind && x.kind !== 'quote' && x.preview?.name && !x.preview.error && !x.preview.already_filed && !alreadyHome(x);
const takenBefore = (list, key) => {
  const names = []; const bases = [];
  for (const x of list) { if (x.key === key) break; if (takesAPlace(x)) { names.push(x.preview.name); bases.push(x.preview.base_name || x.preview.name); } }
  return { names, bases };
};
const sigOf = (e, taken) => [e.kind, e.transaction_id || '', e.interest_id || '', e.sha || '', e.replaces || '', taken.names.join('\u0001'), taken.bases.join('\u0001')].join('|');

export default function useDocumentDrop(dealId) {
  const [entries, setEntries] = useState([]);
  const [context, setContext] = useState(null);
  const [interests, setInterests] = useState({ rows: [], error: null });
  const asked = useRef(new Map());
  const loaded = useRef(false);

  const set = useCallback((key, patch) => setEntries((list) => list.map((e) => (e.key === key ? { ...e, ...(typeof patch === 'function' ? patch(e) : patch) } : e))), []);

  const load = useCallback(() => {
    if (loaded.current || !dealId) return;
    loaded.current = true;
    getDropContext(dealId).then((c) => setContext(c)).catch((e) => setContext({ error: e?.body?.error || e?.message || 'could not read the deal\'s ledger rows', code: e?.body?.code || null }));
    listDealInterests(dealId).then((r) => setInterests({ rows: r?.interests || [], error: null })).catch((e) => setInterests({ rows: [], error: e?.message || 'could not read the interests' }));
  }, [dealId]);

  const add = useCallback((files, { kind = null } = {}) => {
    const list = [...(files || [])];
    if (!list.length) return;
    load();
    const fresh = list.map((file) => ({ key: `drop-${(seq += 1)}`, file, kind: kind || suggestDropKind(file.name), transaction_id: '', interest_id: '', signed_on: '', replaces: undefined, sha: null, preview: null, result: null }));
    setEntries((l) => [...l, ...fresh]);
    for (const e of fresh) sha256OfFile(e.file).then((sha) => set(e.key, { sha: sha || '' }));
  }, [load, set]);

  const clear = useCallback(() => { setEntries([]); asked.current = new Map(); }, []);
  // A file taken back before it is filed (the sheet's remove button); a filed one stays, with its answer.
  const remove = useCallback((key) => { setEntries((l) => l.filter((e) => e.key !== key || e.result?.ok)); asked.current.delete(key); }, []);

  // The preview: asked again whenever the kind, the second answer or the hash changes. A quote and an unchosen kind ask nothing.
  const refuse = useCallback((e, why) => {
    asked.current.delete(e.key);
    setEntries((l) => l.filter((x) => x.key !== e.key));
    toast.error(`${e.file.name} was not added: ${why}`);
  }, []);

  useEffect(() => {
    for (const e of entries) {
      // A file with no kind yet is asked too: the server says whether its bytes are our own paper before the broker picks.
      if (e.sha === null || e.result?.ok) continue;
      const taken = takenBefore(entries, e.key);
      const sig = sigOf(e, taken);
      if (asked.current.get(e.key) === sig) continue;
      asked.current.set(e.key, sig);
      // A quote is asked without its kind (it is parsed, never named here) so the file's own facts are still checked.
      previewDocumentName(dealId, { kind: e.kind === 'quote' ? '' : e.kind, transaction_id: e.transaction_id, interest_id: e.interest_id, sha256: e.sha, mime: e.file.type || '', filename: e.file.name, size: e.file.size, replaces: e.replaces || '',
        taken: taken.names.length ? JSON.stringify(taken.names) : '', taken_base: taken.bases.length ? JSON.stringify(taken.bases) : '' })
        .then((p) => {
          if (asked.current.get(e.key) !== sig) return;
          if (p?.storable && p.storable.ok === false) { refuse(e, p.storable.why); return; }
          // Our own paper (R5): a carrier kind preselected for it is withdrawn, so the broker chooses again from what is offered;
          // so is a kind this file cannot be (kind_rules), with the reason on the row.
          set(e.key, (cur) => ({ preview: p, kind: (p.our_paper && dropKind(cur.kind)?.carrier) || (cur.kind && p.kind_rules?.[cur.kind]?.offered === false) ? '' : cur.kind,
            replaces: p.replaces?.length ? (p.replaces.some((c) => c.id === cur.replaces) || cur.replaces === 'none' ? cur.replaces : undefined) : undefined }));
        })
        // A failed preview never refuses the file (only storable does, above): the row says it could not be checked, and the file
        // is neither counted nor posted (`pending`). The door's expected answers (DROP_EXPECTED) toast nothing; any other failure
        // api.js has shown once, so there is never a second toast. No policy record is the deal's answer, said once above the rows.
        .catch((err) => {
          if (asked.current.get(e.key) !== sig) return;
          const code = err?.body?.code || null;
          const why = code === NO_POLICY_ROW ? 'not checked: this deal has no policy record yet (said above)' : `could not be checked: ${err?.body?.error || err?.message || 'the server did not answer'}`;
          set(e.key, { preview: { error: why, code } });
        });
    }
  }, [entries, dealId, set, refuse]);

  // An application asks the day it was signed (David Q6, round 2): the server refuses a drop without it, so it is asked here;
  // a day after the server's latest (signed_on_latest, today Eastern) is not an answer either (review fix, round 2).
  const needsAnswer = (e) => !isSettled(e) && (!e.kind
    || signedOnProblem(e, context) !== null
    || (e.preview?.kind_rules?.[e.kind]?.offered === false)
    || (e.kind !== 'quote' && (e.sha === null || !e.preview || e.preview.error
      || (e.preview.replaces?.length && e.replaces === undefined)
      || (e.preview.our_paper && dropKind(e.kind)?.carrier))));
  // A file whose preview failed for a reason other than a refusal is not counted or posted (its row says so); refused files are
  // already off the list. `pending` is what the File button counts and what is posted.
  const pending = entries.filter((e) => !isSettled(e) && !e.preview?.error);
  // The deal has no policy record: one sentence for the whole drop (the drop context's, else the first file's answer).
  const noPolicy = context?.code === NO_POLICY_ROW ? context.error : (entries.some((e) => e.preview?.code === NO_POLICY_ROW) ? 'This deal has no policy record in TRACON yet, so nothing can be filed on it.' : null);
  const ready = pending.length > 0 && !pending.some(needsAnswer);
  // The first unanswered file and the question it still needs, in words (Chrome pass, round 2: the sheet's toast said only
  // "what it is, and whether it replaces one already filed" when the question left was the signing day).
  const firstUnanswered = () => { const e = pending.find(needsAnswer); return e ? `${e.file.name}: ${questionLeft(e, context)}` : null; };

  // The server's per-file answers, in the order the files were posted.
  const applyResults = useCallback((posted, results) => {
    posted.forEach((e, i) => set(e.key, { result: results[i] || { ok: false, error: 'no answer for this file' } }));
  }, [set]);

  // "Wrong type? Change it": the filed row re-typed through placeDocument, the answer read back from the server.
  // N5: Signed application states the signing day (extra.signed_on); a partial filing is said on the row.
  const retype = useCallback(async (key, kind, extra = {}) => {
    const e = entries.find((x) => x.key === key);
    const id = e?.result?.document?.id;
    if (!id) return;
    set(key, { retyping: true, retypeError: null });
    try {
      const res = await placeDealDocument(id, { kind, transaction_id: kind === 'application' ? undefined : (e.transaction_id || undefined), ...extra });
      set(key, (cur) => ({ retyping: false, retypeError: res?.partial || null, result: { ...cur.result, document: res.document } }));
      announceDocumentsChanged(dealId);
    } catch (err) {
      set(key, { retyping: false, retypeError: err?.body?.error || err?.message || 'could not change the type' });
    }
  }, [entries, set, dealId]);

  return { dealId, entries, pending, add, set, clear, remove, ready, applyResults, retype, context, interests, needsAnswer, firstUnanswered, noPolicy };
}

/**
 * The bytes are already filed and nothing a drop could state would change that row (the preview's own rule), or (David Q8,
 * round 2) the file is a signed application whose bytes are already uploaded to this deal (the preview's application_on_file).
 */
export function alreadyHome(e) {
  return !e.result?.ok && (e.preview?.already_filed?.settled === true || (e.kind === 'application' && !!e.preview?.application_on_file));
}
const isSettled = (e) => !!e.result?.ok || alreadyHome(e);

/**
 * What is wrong with an application's signing day, in words, or null (also null for any other kind). The latest day is the
 * server's (drop-context signed_on_latest); without it only the blank is caught here and the server refuses a later day.
 */
export function signedOnProblem(e, context) {
  if (e.kind !== 'application') return null;
  if (!e.signed_on) return 'Needed before it can be filed';
  const latest = context?.signed_on_latest;
  if (latest && e.signed_on > latest) return 'A signing day cannot be after today';
  return null;
}

/** The question a file still needs, in words, in the order needsAnswer asks them. */
function questionLeft(e, context) {
  if (!e.kind) return 'say what it is';
  const day = signedOnProblem(e, context);
  if (day === 'Needed before it can be filed') return 'say the day it was signed (the date on the application)';
  if (day) return day.charAt(0).toLowerCase() + day.slice(1);
  if (e.preview?.kind_rules?.[e.kind]?.offered === false) return e.preview.kind_rules[e.kind].why;
  if (e.kind === 'quote') return 'say what it is';
  if (e.preview?.error) return `it ${e.preview.error}; remove it and drop it again`;
  if (e.sha === null || !e.preview) return 'it is still being read; try again in a moment';
  if (e.preview.our_paper && dropKind(e.kind)?.carrier) return 'it is our own paper, not the carrier\'s: choose another type';
  if (e.preview.replaces?.length && e.replaces === undefined) return `say whether it replaces ${e.preview.replaces.length === 1 ? e.preview.replaces[0].name : 'one already filed'}`;
  return 'say what it is';
}

// Each entry posted as the door's item: the replace answer is sent only when the server asked the question.
export function itemOf(e) {
  return { file: e.file, kind: e.kind, transaction_id: e.transaction_id || undefined, interest_id: e.interest_id || undefined, signed_on: e.kind === 'application' ? e.signed_on || undefined : undefined, replaces_document_id: e.preview?.replaces?.length ? e.replaces : undefined };
}
